import { useCallback, useEffect, useMemo, useState } from 'react';
import { groupMovements } from '../api/endpointsBackend';
import { usePreferences } from '../contexts/PreferencesContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usePhoneShell } from '../components/phone/PhoneShellContext';
import { reviewFilters } from '../utils/movementAlerts';

// The tab bar, Home and Review each ask for the same count at the same moment;
// one request answers all of them. An entry lives for a few seconds, so a
// later refresh (a save, the tab regaining focus) still asks the server.
const SHARE_MS = 3000;
const shared = new Map();

// The last count each scope answered, so a page that mounts again (Home after
// a trip to Movements) starts on the last number rather than on a blank.
const lastKnown = new Map();
const scopeKey = (filters, start, end, workspaceId) => JSON.stringify([filters, start, end, workspaceId]);

const sumMovements = (rows) => (rows || []).reduce((sum, row) => sum + (row.num_movements || 0), 0);

function countOnce(filters, start, end, workspaceId, signal) {
  if (!filters) return Promise.resolve(0);
  // The refresh signal is part of the key: after a save, every count asks again.
  const key = JSON.stringify([filters, start, end, workspaceId, signal]);
  const hit = shared.get(key);
  if (hit && Date.now() - hit.at < SHARE_MS) return hit.promise;
  // Grouping requires a dimension; by currency, then summed, is the count.
  const promise = groupMovements(filters, ['currency'], start, end).then(sumMovements);
  shared.set(key, { at: Date.now(), promise });
  promise.catch(() => shared.delete(key));
  return promise;
}

/**
 * How many movements need the user (`fixable`) and how many are only waiting
 * for their bank row (`waiting`), under the user's alert preferences. Feeds
 * the Home tab's badge, Home's review row and Review's subtitle.
 *
 * All time by default: Review is an inbox, and its count reaches zero only
 * when nothing is left. `{ start, end }` bounds it to a period instead (the
 * Movements "To review" chip, so its number matches the rows it filters to).
 * Refetched on launch, when the tab regains focus, and after every phone save
 * or fix.
 *
 * @returns {{ fixable: number, waiting: number, loading: boolean, refresh: Function }}
 */
export default function useReviewCount({ start = null, end = null } = {}) {
  const { alerts: prefs } = usePreferences();
  const { currentId } = useWorkspace();
  const { refreshSignal } = usePhoneShell();
  const filters = useMemo(() => reviewFilters(prefs), [prefs]);
  const scope = scopeKey(filters, start, end, currentId);
  // `loading` stays true only until the first count of this scope arrives.
  const [state, setState] = useState(() => {
    const last = lastKnown.get(scope);
    return last ? { ...last, loading: false } : { fixable: 0, waiting: 0, loading: true };
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (currentId == null) return undefined;
    let active = true;
    Promise.all([
      countOnce(filters.fixable, start, end, currentId, refreshSignal),
      countOnce(filters.waiting, start, end, currentId, refreshSignal),
    ])
      .then(([fixable, waiting]) => {
        lastKnown.set(scope, { fixable, waiting });
        if (active) setState({ fixable, waiting, loading: false });
      })
      .catch(() => {
        if (active) setState((prev) => ({ ...prev, loading: false }));
      });
    return () => { active = false; };
  }, [filters, start, end, currentId, scope, refreshSignal, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setTick((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const refresh = useCallback(() => {
    shared.clear();
    setTick((n) => n + 1);
  }, []);

  return { ...state, refresh };
}
